import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Header } from "@/components/app/Header";
import { Oracle } from "@/components/app/Oracle";
import { LatestDraw } from "@/components/app/LatestDraw";
import { Analytics } from "@/components/app/Analytics";
import { History } from "@/components/app/History";
import { SavedPicks } from "@/components/app/SavedPicks";
import { Paywall } from "@/components/app/Paywall";
import { DrawSchedule } from "@/components/app/DrawSchedule";
import { RenewBanner } from "@/components/app/RenewBanner";
import { JackpotBanner } from "@/components/app/JackpotBanner";
import { Scoreboard } from "@/components/app/Scoreboard";
import { MobileNav } from "@/components/app/MobileNav";
import { Toaster } from "@/components/ui/sonner";
import { useAccess } from "@/context/AccessContext";

export default function Dashboard() {
  const { active, kind, loading, subscribe, openPortal, refresh } = useAccess();
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    if (loading) return;
    if (params.get("portal") === "1") {
      setParams({}, { replace: true });
      refresh(true);
      return;
    }
    if (params.get("manage") === "1") {
      setParams({}, { replace: true });
      if (active && kind === "subscription") openPortal().catch(() => {});
      else document.getElementById("oracle")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    if (params.get("renew") !== "1") return;
    setParams({}, { replace: true });
    if (active && kind === "subscription") openPortal().catch(() => {});
    else if (active) subscribe().catch(() => {});
    else document.getElementById("oracle")?.scrollIntoView({ behavior: "smooth" });
  }, [loading, active, kind, params, setParams, refresh, openPortal, subscribe]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <div className="min-h-screen bg-ink text-parchment pb-20 md:pb-0">
      <JackpotBanner />
      <Header onScrollTo={scrollTo} />
      <RenewBanner />
      {loading ? (
        <div data-testid="access-loading" className="py-24 text-center label-caps text-parchment/40">Consulting the ledger…</div>
      ) : active ? (
        <>
          <Oracle />
          <LatestDraw />
          <DrawSchedule />
          <Scoreboard />
          <Analytics />
          <History />
          <SavedPicks />
        </>
      ) : (
        <>
          <Paywall />
          <LatestDraw />
          <DrawSchedule />
          <Scoreboard />
        </>
      )}
      <footer className="border-t border-white/5 mt-12 py-8 text-center font-body text-xs text-parchment/40">
        Powerball draws are cryptographically random. Data-driven analytics only — play responsibly.
        <span className="mx-2 text-parchment/20">·</span>
        Data source: NY State Open Data (data.ny.gov)
        <span className="mx-2 text-parchment/20">·</span>
        <Link to="/promo" data-testid="footer-promo-link" className="hover:text-oracle transition-colors">Promo Kit</Link>
        <span className="mx-2 text-parchment/20">·</span>
        <Link to="/admin" data-testid="footer-admin-link" className="hover:text-oracle transition-colors">Owner Ledger</Link>
        <div className="mt-3 flex items-center justify-center gap-2">
          <Link to="/legal/terms" data-testid="footer-terms-link" className="hover:text-oracle transition-colors">Terms</Link>
          <span className="text-parchment/20">·</span>
          <Link to="/legal/privacy" data-testid="footer-privacy-link" className="hover:text-oracle transition-colors">Privacy</Link>
          <span className="text-parchment/20">·</span>
          <Link to="/legal/refunds" data-testid="footer-refunds-link" className="hover:text-oracle transition-colors">Refunds</Link>
          <span className="text-parchment/20">·</span>
          <span>18+ only</span>
        </div>
      </footer>
      <MobileNav onScrollTo={scrollTo} />
      <Toaster position="top-right" theme="dark" mobileOffset={{ top: 72 }} />
    </div>
  );
}
